import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PolicyBody } from "@/lib/policyContent.jsx";
import { ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * PolicyAcceptanceDialog
 *
 * Modal that forces the user to read and accept the site policy before
 * continuing with a gated action (e.g., creating their first hive).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - onAccepted: () => void  // called after backend records acceptance
 */
export default function PolicyAcceptanceDialog({ open, onOpenChange, onAccepted }) {
  const { refreshMe } = useAuth();
  const [checked, setChecked] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setChecked(false);
      setScrolledToEnd(false);
      // Reset scroll position on next paint.
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      });
    }
  }, [open]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    // Treat as fully read when within 24px of the bottom.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setScrolledToEnd(true);
    }
  };

  const handleAccept = async () => {
    if (!checked || !scrolledToEnd || saving) return;
    setSaving(true);
    try {
      await api.post("/auth/accept-policy");
      await refreshMe();
      toast.success("Thanks \u2014 terms accepted.");
      onOpenChange(false);
      onAccepted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your acceptance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onOpenChange(false); }}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="policy-acceptance-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200 bg-amber-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-400 grid place-items-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-amber-900" />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-medium">Before you create your first hive</DialogTitle>
              <DialogDescription className="text-stone-600 mt-1 text-sm">
                Please read our Terms of Use and Content Policy in full. You must agree before continuing.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="px-6 py-5 max-h-[55vh] overflow-y-auto border-b border-stone-100"
          data-testid="policy-acceptance-scroll"
        >
          <PolicyBody />
          <div className="mt-8 text-center text-xs text-stone-400" data-testid="policy-acceptance-end">
            — End of Terms —
          </div>
        </div>

        {!scrolledToEnd && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 text-center" data-testid="policy-scroll-hint">
            Please scroll to the bottom to read the terms in full.
          </div>
        )}

        <div className="px-6 py-4 flex items-start gap-3">
          <Checkbox
            id="policy-accept-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(Boolean(v))}
            disabled={!scrolledToEnd}
            data-testid="policy-accept-checkbox"
            className="mt-0.5"
          />
          <label
            htmlFor="policy-accept-checkbox"
            className={`text-sm leading-relaxed cursor-pointer select-none ${scrolledToEnd ? "text-stone-800" : "text-stone-400"}`}
          >
            I have read the{" "}
            <Link to="/policy" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline inline-flex items-center gap-0.5">
              Terms of Use & Content Policy <ExternalLink className="w-3 h-3" />
            </Link>{" "}
            in their entirety, and I agree to be bound by them. I understand that uploads must be bee-related and that
            harmful, illicit, illegal, or offensive content is strictly prohibited and will be reported and prosecuted to
            the fullest extent of the law.
          </label>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-stone-200 bg-stone-50 flex sm:justify-between gap-2">
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="policy-decline-button"
          >
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!checked || !scrolledToEnd || saving}
            className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 disabled:opacity-50"
            data-testid="policy-accept-button"
          >
            {saving ? "Saving\u2026" : "I accept \u2014 continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
